import React, { useState } from 'react';
import { Plus, Settings, ShieldAlert, Trash2 } from 'lucide-react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../hooks/useLanguage';
import { useTheme } from '../../hooks/useTheme';
import { WorkspaceLogo } from '../common/WorkspaceLogo';
import { UserAvatar } from '../common/UserAvatar';
import { ConfirmDeleteModal } from '../modals/ConfirmDeleteModal';

interface WorkspaceRailProps {
  onOpenCreateWorkspace: () => void;
  onOpenProfile: () => void;
  onOpenSettings: () => void;
}

export const WorkspaceRail: React.FC<WorkspaceRailProps> = ({
  onOpenCreateWorkspace,
  onOpenProfile,
  onOpenSettings
}) => {
  const { workspaces, currentWorkspace, selectWorkspace, deleteWorkspace, members } = useWorkspace();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { theme, customTokens } = useTheme();
  const [deleteTargetWorkspace, setDeleteTargetWorkspace] = useState<{ id: string; name: string } | null>(null);

  const currentUserMember = members.find((m) => m.user.id === user?.id);

  const isCustomTheme = theme === 'custom';
  const railBackground = isCustomTheme
    ? customTokens.miniSidebarBackground
    : '#1a1b4b';

  return (
    <aside
      className={`w-20 border-r flex flex-col items-center pt-4 pb-3 select-none z-20 flex-shrink-0 transition-colors duration-200 ${
        railBackground ? '' : 'bg-sidebar border-sidebar-border'
      }`}
      style={
        railBackground
          ? {
              backgroundColor: railBackground,
              borderColor: isCustomTheme ? customTokens.sidebarBorder : '#292b6a',
            }
          : undefined
      }
    >
      {/* Workspace list */}
      <div className="flex-1 flex flex-col items-center gap-3 w-full overflow-y-auto no-scrollbar pt-2 pb-1">
        {workspaces.map((ws) => {
          const isActive = currentWorkspace?.id === ws.id;
          const isWsAdmin =
            user?.globalRole === 'SUPER_ADMIN' ||
            ws.role === 'ADMIN' ||
            ws.role === 'OWNER' ||
            ws.ownerId === user?.id ||
            (isActive && (currentUserMember?.role === 'ADMIN' || currentUserMember?.role === 'OWNER'));

          return (
            <div key={ws.id} className="relative group flex items-center justify-center w-full">
              {/* Active Indicator Bar */}
              <div
                className={`absolute left-0 w-1.5 bg-primary rounded-r transition-all duration-200 ${
                  isActive ? 'h-10' : 'h-0 group-hover:h-5'
                }`}
              />

              <button
                onClick={() => {
                  if (isActive) {
                    onOpenSettings();
                  } else {
                    selectWorkspace(ws.id);
                  }
                }}
                title={`${ws.name} • ${isActive ? 'Click for Workspace Settings & Logo' : 'Switch workspace'}`}
                aria-label={`Workspace ${ws.name}`}
                className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-200 shadow-sm overflow-hidden cursor-pointer ${
                  isActive
                    ? `rounded-xl ring-2 ring-primary ring-offset-2 ${
                        railBackground ? 'ring-offset-transparent' : 'ring-offset-sidebar'
                      } scale-105 shadow-md hover:ring-primary/80`
                    : 'hover:rounded-xl hover:scale-105 opacity-85 hover:opacity-100'
                }`}
              >
                <WorkspaceLogo
                  workspaceId={ws.id}
                  name={ws.name}
                  avatarUrl={ws.avatarUrl}
                  size="lg"
                  className="w-full h-full text-lg"
                />
              </button>

              {/* Admin delete button on hover */}
              {isWsAdmin && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteTargetWorkspace({ id: ws.id, name: ws.name });
                  }}
                  title={`Delete Workspace "${ws.name}"`}
                  aria-label={`Delete Workspace "${ws.name}"`}
                  className="absolute -top-1 -right-0.5 opacity-0 group-hover:opacity-100 p-1 rounded-full bg-destructive text-white shadow-md hover:scale-110 hover:bg-destructive/90 transition-all z-30 cursor-pointer"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}

        {/* Add Workspace Button */}
        <button
          onClick={onOpenCreateWorkspace}
          title={t('navigation.addWorkspace')}
          aria-label={t('navigation.addWorkspace')}
          className={`w-12 h-12 rounded-2xl border-2 border-dashed flex items-center justify-center transition-all duration-200 hover:rounded-xl hover:scale-105 group ${
            railBackground
              ? 'border-white/25 hover:border-white/60 text-white/70 hover:text-white bg-white/5 hover:bg-white/10'
              : 'border-border hover:border-primary text-muted-foreground hover:text-primary bg-card/40 hover:bg-card'
          }`}
        >
          <Plus className="h-5 w-5 transition-transform group-hover:rotate-90" />
        </button>
      </div>

      {/* Bottom Profile & Settings */}
      <div
        className={`flex flex-col items-center gap-2 pt-3 border-t w-full ${
          railBackground ? 'border-white/15' : 'border-sidebar-border'
        }`}
      >
        {currentWorkspace && (
          <button
            onClick={onOpenSettings}
            title={t('navigation.settings')}
            aria-label={t('navigation.settings')}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition ${
              railBackground
                ? 'text-white/70 hover:text-white hover:bg-white/10'
                : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
            }`}
          >
            <Settings className="h-5 w-5" />
          </button>
        )}

        <button
          onClick={onOpenProfile}
          title={user?.displayName || 'User Profile'}
          aria-label={user?.displayName || 'User Profile'}
          className="rounded-xl transition hover:scale-105 shrink-0"
        >
          <UserAvatar
            userId={user?.id}
            displayName={user?.displayName}
            avatarUrl={user?.avatarUrl}
            size="md"
            showStatus
            status={user?.status}
            className="shadow-sm"
          />
        </button>

        {user?.globalRole === 'SUPER_ADMIN' && (
          <button
            onClick={() => (window.location.href = '/platform')}
            title="Platform Administration"
            aria-label="Platform Administration"
            className={`w-10 h-10 mt-2 rounded-xl flex items-center justify-center transition ${
              railBackground
                ? 'bg-purple-400/20 text-purple-200 hover:bg-purple-500 hover:text-white'
                : 'bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500 hover:text-white'
            }`}
          >
            <ShieldAlert className="h-5 w-5" />
          </button>
        )}
      </div>

      {deleteTargetWorkspace && (
        <ConfirmDeleteModal
          isOpen={true}
          onClose={() => setDeleteTargetWorkspace(null)}
          type="workspace"
          itemName={deleteTargetWorkspace.name}
          onConfirm={async () => {
            await deleteWorkspace(deleteTargetWorkspace.id);
          }}
        />
      )}
    </aside>
  );
};
